"use client";

import {
  Box,
  Grid,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  Progress,
} from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import type { AnalysisResult } from "../mockdata/analysisData";
import { getScoreColor } from "../utils/helpers";
import { Emoji } from "@/_components/ui/Emoji";
import type { EmojiKey } from "@/_components/ui/Emoji/emojiList";
import { OverviewRanking } from "./OverviewRanking";

interface OverviewTabProps {
  data: AnalysisResult;
  rankingPercentile: number | null;
  rankingIndustry: string | null;
  shareUrl?: string;
}

const SCORECARD_META: Array<{
  key: keyof AnalysisResult["scorecards"];
  labelKey:
    | "overall"
    | "experience"
    | "skills"
    | "language"
    | "education"
    | "certificates";
  icon: EmojiKey;
}> = [
  { key: "overall", labelKey: "overall", icon: "glowing-star" },
  { key: "experiences", labelKey: "experience", icon: "fire" },
  { key: "skills", labelKey: "skills", icon: "briefcase" },
  {
    key: "language",
    labelKey: "language",
    icon: "globe-showing-europe-africa",
  },
  { key: "education", labelKey: "education", icon: "graduation-cap" },
  { key: "certificates", labelKey: "certificates", icon: "scroll" },
];

export const OverviewTab = ({
  data,
  rankingPercentile,
  rankingIndustry,
  shareUrl,
}: OverviewTabProps) => {
  const t = useTranslations("resume_benchmark");
  const overall = data.scorecards.overall;
  const highPriorityCount = data.improvements.filter(
    (i) => i.priority === "high",
  ).length;

  return (
    <VStack gap={8} align="stretch">
      <OverviewRanking
        overall={overall}
        candidateName={data.candidateName}
        candidateTitle={data.candidateTitle}
        aiSummary={data.aiSummary}
        topPercent={rankingPercentile}
        industry={rankingIndustry}
        shareUrl={shareUrl}
      />

      <SimpleGrid columns={{ base: 2, md: 3 }} gap={3}>
        {SCORECARD_META.map(({ key, labelKey, icon }) => {
          const score = data.scorecards[key];
          const color = getScoreColor(score);
          return (
            <Box
              key={key}
              p={4}
              rounded="xl"
              bg="whiteAlpha.50"
              border="1px solid"
              borderColor="whiteAlpha.100"
              _hover={{ borderColor: "whiteAlpha.200", bg: "whiteAlpha.100" }}
              transition="all 0.2s"
            >
              <HStack gap={2} mb={3}>
                <Emoji emoji={icon} size={5} />
                <Text fontSize="xs" color="neutral.400" fontWeight="medium">
                  {t(`overview.scorecards.${labelKey}`)}
                </Text>
              </HStack>
              <Text
                fontSize="3xl"
                fontWeight="medium"
                color="neutral.100"
                fontFamily="mono"
                mb={2}
              >
                {score}
                <Text
                  as="span"
                  fontSize="xs"
                  color="neutral.300"
                  fontWeight="normal"
                  fontFamily="mono"
                >
                  {" "}
                  {t("overview.scoreOutOfCompact")}
                </Text>
              </Text>
              <Progress.Root value={score} colorPalette={color} size="xs">
                <Progress.Track bg="whiteAlpha.100" rounded="full">
                  <Progress.Range rounded="full" />
                </Progress.Track>
              </Progress.Root>
            </Box>
          );
        })}
      </SimpleGrid>

      <Box
        p={6}
        rounded="2xl"
        bg="whiteAlpha.50"
        border="1px solid"
        borderColor="whiteAlpha.100"
        id="ai-summary"
      >
        <HStack gap={2} mb={3}>
          <Emoji emoji="robot" size={5} />
          <Text fontSize="sm" fontWeight="medium" color="neutral.100">
            {t("overview.aiSummary")}
          </Text>
        </HStack>
        <Text fontSize="sm" color="neutral.300" lineHeight="1.8">
          {data.aiSummary}
        </Text>
      </Box>

      <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={3}>
        <Box
          p={5}
          rounded="xl"
          bg="whiteAlpha.50"
          border="1px solid"
          borderColor="whiteAlpha.100"
        >
          <Text fontSize="xs" color="neutral.400" mb={1}>
            {t("overview.atsScore")}
          </Text>
          <Text
            fontSize="3xl"
            fontWeight="medium"
            color={`${getScoreColor(data.atsScore)}.400`}
            fontFamily="mono"
          >
            {data.atsScore}
            <Text
              as="span"
              fontSize="sm"
              color="neutral.400"
              fontWeight="normal"
              fontFamily="mono"
            >
              {t("overview.scoreOutOfCompact")}
            </Text>
          </Text>
          <Text fontSize="xs" color="warning.500" mt={1}>
            {t("overview.issuesDetected", { count: data.atsIssues.length })}
          </Text>
        </Box>
        <Box
          p={5}
          rounded="xl"
          bg="whiteAlpha.50"
          border="1px solid"
          borderColor="whiteAlpha.100"
        >
          <Text fontSize="xs" color="neutral.400" mb={1}>
            {t("overview.improvementItems")}
          </Text>
          <Text
            fontSize="3xl"
            fontWeight="medium"
            fontFamily="mono"
            color="warning.500"
          >
            {data.improvements.length}
          </Text>
          <Text fontSize="xs" color="error.400" mt={1}>
            {t("overview.highPriority", { count: highPriorityCount })}
          </Text>
        </Box>
      </Grid>
    </VStack>
  );
};
